import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface ApiResponse<T> {
  status: boolean;
  message: string;
  data: T;
  totalRecords?: number; // For pagination
}

export interface MasterItem {
  id: string | number;
  value: string;
  code?: string;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class MasterService {
  private apiUrl = `${environment.apiBaseUrl}masters`;

  constructor(private http: HttpClient) {}

  // GET /api/masters?type=Country
  getCountries(): Observable<ApiResponse<MasterItem[]>> {
    const params = new HttpParams().set('DdlType', 'Country');
    return this.http.get<ApiResponse<MasterItem[]>>(this.apiUrl, { params });
  }

  // GET /api/masters?type=State
  getStates(countryId?: string | number): Observable<ApiResponse<MasterItem[]>> {
    let params = new HttpParams().set('DdlType', 'States');
    console.log('Fetching states with countryId:', countryId);
    if (countryId) {
      params = params.set('countryId', countryId.toString());
    }
    return this.http.get<ApiResponse<MasterItem[]>>(this.apiUrl, { params });
  }

  // GET /api/masters?type=City
  getCities(stateId?: string | number, countryId?: string | number): Observable<ApiResponse<MasterItem[]>> {
    let params = new HttpParams().set('DdlType', 'City');
    if (stateId) {
      params = params.set('FilterValue', stateId.toString());
    }
    if (countryId) {
      params = params.set('countryId', countryId.toString());
    }
    return this.http.get<ApiResponse<MasterItem[]>>(this.apiUrl, { params });
  }

  // GET /api/masters?type=DdlTypes (for various dropdown types)
  getDdlTypes(ddlType: string, clinicId?: number): Observable<ApiResponse<MasterItem[]>> {
    let params = new HttpParams().set('DdlType', ddlType);
    if (clinicId !== undefined && clinicId !== null) {
      params = params.set('ClinicId', clinicId.toString());
    }
    return this.http.get<ApiResponse<MasterItem[]>>(this.apiUrl, { params });
  }

  // GET /api/masters?type=DdlTypes with search filtering
  getDdlTypesWithSearch(ddlType: string, clinicId?: number, filterValue?: string): Observable<ApiResponse<MasterItem[]>> {
    let params = new HttpParams().set('DdlType', ddlType);
    if (clinicId !== undefined && clinicId !== null) {
      params = params.set('ClinicId', clinicId.toString());
    }
    if (filterValue) {
      params = params.set('FilterValue', filterValue);
    }
    return this.http.get<ApiResponse<MasterItem[]>>(this.apiUrl, { params });
  }

  // Generic method to get any master data type
  getMasterData(type: string, additionalParams?: { [key: string]: string | number }): Observable<ApiResponse<MasterItem[]>> {
    let params = new HttpParams().set('DdlType', type);
    
    if (additionalParams) {
      Object.keys(additionalParams).forEach(key => {
        params = params.set(key, additionalParams[key].toString());
      });
    }
    
    return this.http.get<ApiResponse<MasterItem[]>>(this.apiUrl, { params });
  }

  // GET paginated code list (e.g. CPT, HCPCS, ICD, Revenue, Custom)
  getCodeList(codeType: string, start: number, pageSize: number, userId: string, clinicId: number, searchTerm?: string): Observable<ApiResponse<MasterItem[]>> {
    const url = `${environment.apiBaseUrl}codes/${codeType}/getlist`;
    let params = new HttpParams()
      .set('Start', start.toString())
      .set('PageSize', pageSize.toString())
      .set('UserId', userId)
      .set('ClinicId', clinicId.toString());

    if (searchTerm) {
      params = params.set('code', searchTerm);
    }

    return this.http.get<ApiResponse<MasterItem[]>>(url, { params });
  }
}
